/* oxlint-disable unicorn/prefer-set-has -- LINTER migration follow up */
import {
  ALL_BID_ROWS_OPTIONAL_ERROR_CODE,
  FROZEN_PRICE_MODIFICATION_ERROR_CODE,
  MISSING_FROZEN_PRICE_ERROR_CODE,
  OPTIONAL_BID_ROWS_NOT_ALLOWED_IN_DEAL_ERROR_CODE,
  OPTIONAL_ROWS_REQUIRE_FREE_OR_ALL_FORCED_ERROR_CODE,
} from "@grafit/shared/domain/bid-row/bid-row.errors";
import { SESSION_CHANGED_ERROR_CODE, VERSION_READ_ONLY_ERROR_CODE } from "@grafit/shared/domain/error/errors";
import { DUPLICATE_EXPORT_TEMPLATE_NAME_ERROR_CODE } from "@grafit/shared/domain/organisation/export-template/export-template.errors";
import { INTERNAL_REFERENCE_SERIES_EXHAUSTED_ERROR_CODE } from "@grafit/shared/domain/organisation/internal-reference-series";
import {
  DUPLICATE_INTERNAL_QUOTE_REFERENCE_ERROR_CODE,
  PROJECT_ALREADY_ACTIVE_ERROR_CODE,
  PROJECT_DELETED_ERROR_CODE,
} from "@grafit/shared/domain/project/project.errors";
import { DUPLICATE_SUPPLIER_CONTACT_EMAIL_ERROR_CODE } from "@grafit/shared/domain/supplier-contact/organisation-supplier-contact.errors";
import { TRPCClientError } from "./trpc";

/**
 * This covers both Error instances and TRPCClientErrorLike types.
 */
export interface TrpcErrorLike {
  message: string;
  data?: unknown;
}

const TRPC_40X_ERRORS = ["FORBIDDEN", "BAD_REQUEST", "NOT_FOUND", "UNAUTHORIZED"];
const BID_ROW_MUTATION_CUSTOM_MESSAGE_ERROR_CODES = [
  FROZEN_PRICE_MODIFICATION_ERROR_CODE,
  MISSING_FROZEN_PRICE_ERROR_CODE,
  OPTIONAL_BID_ROWS_NOT_ALLOWED_IN_DEAL_ERROR_CODE,
  OPTIONAL_ROWS_REQUIRE_FREE_OR_ALL_FORCED_ERROR_CODE,
  ALL_BID_ROWS_OPTIONAL_ERROR_CODE,
] as const;

export type BidRowMutationCustomMessageErrorCode = (typeof BID_ROW_MUTATION_CUSTOM_MESSAGE_ERROR_CODES)[number];

const getResponseErrorField = (error: TrpcErrorLike, field: string): unknown => {
  return "data" in error && error.data instanceof Object && field in error.data
    ? (error.data as Record<typeof field, unknown>)[field]
    : undefined;
};

const isAppError = (error: TrpcErrorLike, statusCheck: (status: number) => boolean): boolean => {
  const httpStatus = getResponseErrorField(error, "httpStatus");
  return typeof httpStatus === "number" && statusCheck(httpStatus);
};

const isApp401Error = (error: TrpcErrorLike): boolean => isAppError(error, (status) => status === 401);

export const isApp400Error = (error: TrpcErrorLike): boolean =>
  isAppError(error, (status) => status >= 400 && status < 500);

export const isTRPC40XError = (error: Error): boolean => {
  if (error instanceof TRPCClientError) {
    return TRPC_40X_ERRORS.includes(error.message);
  }
  return false;
};

export const isUnauthorized = (error: TrpcErrorLike): boolean => {
  if (error instanceof TRPCClientError) {
    const code = getResponseErrorField(error, "code");
    if (error.message === "UNAUTHORIZED" || (code as string | undefined) === "UNAUTHORIZED") {
      return true;
    }
  }

  return isApp401Error(error);
};

export const isBadRequestError = (error: TrpcErrorLike): boolean => {
  if (error instanceof TRPCClientError) {
    const code = getResponseErrorField(error, "code");
    if (error.message === "BAD_REQUEST" || (code as string | undefined) === "BAD_REQUEST") {
      return true;
    }
  }

  return isAppError(error, (status) => status === 400);
};

export const isConflictError = (error: TrpcErrorLike): boolean => {
  if (error instanceof TRPCClientError) {
    const code = getResponseErrorField(error, "code");
    if (error.message === "CONFLICT" || (code as string | undefined) === "CONFLICT") {
      return true;
    }
  }

  return isAppError(error, (status) => status === 409);
};

export const isNotFoundError = (error: TrpcErrorLike): boolean => {
  if (error instanceof TRPCClientError) {
    const code = getResponseErrorField(error, "code");
    if (error.message === "NOT_FOUND" || (code as string | undefined) === "NOT_FOUND") {
      return true;
    }
  }

  return isAppError(error, (status) => status === 404);
};

export const isVersionReadOnlyError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === VERSION_READ_ONLY_ERROR_CODE;
};

export const isSessionChangedError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === SESSION_CHANGED_ERROR_CODE;
};

export const isProjectDeletedError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === PROJECT_DELETED_ERROR_CODE;
};

export const isProjectAlreadyActiveError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === PROJECT_ALREADY_ACTIVE_ERROR_CODE;
};

export const isDuplicateInternalQuoteReferenceError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === DUPLICATE_INTERNAL_QUOTE_REFERENCE_ERROR_CODE;
};

export const isDuplicateExportTemplateNameError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === DUPLICATE_EXPORT_TEMPLATE_NAME_ERROR_CODE;
};

export const isDuplicateSupplierContactEmailError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === DUPLICATE_SUPPLIER_CONTACT_EMAIL_ERROR_CODE;
};

export const isInternalReferenceSeriesExhaustedError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === INTERNAL_REFERENCE_SERIES_EXHAUSTED_ERROR_CODE;
};

/** @internal (knip flags this as un necessary export since it's only imported in a test file) */
export const isFrozenPriceModificationError = (error: TrpcErrorLike): boolean => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return appErrorCode === FROZEN_PRICE_MODIFICATION_ERROR_CODE;
};

export const getBidRowMutationCustomMessageErrorCode = (
  error: TrpcErrorLike,
): BidRowMutationCustomMessageErrorCode | undefined => {
  const appErrorCode = getResponseErrorField(error, "appErrorCode");
  return BID_ROW_MUTATION_CUSTOM_MESSAGE_ERROR_CODES.includes(appErrorCode as BidRowMutationCustomMessageErrorCode)
    ? (appErrorCode as BidRowMutationCustomMessageErrorCode)
    : undefined;
};

export const isNetworkError = (error: TrpcErrorLike): boolean => {
  if (!(error instanceof TRPCClientError)) {
    return false;
  }
  if (error.cause instanceof TypeError) {
    return true;
  }
  return error.message.startsWith("Failed to fetch");
};

/**
 * @returns true if the provided error DOES NOT warrant displaying an error toast and alerting devs
 **/
export const isSilencedError = (error: TrpcErrorLike): boolean => {
  return (
    isUnauthorized(error) ||
    isVersionReadOnlyError(error) ||
    isSessionChangedError(error) ||
    isProjectDeletedError(error) ||
    isNetworkError(error)
  );
};
